import { useState, useCallback, useEffect, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APIError, useAxios } from "../core/_axios";
import LoadingSpinner from "./LoadingSpinner";
import dayjs from "dayjs";
import { calculateTrendLine, generateTrendLineData } from "../utils/trendLineCalculator";
import { Download } from "lucide-react";

import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import weekday from "dayjs/plugin/weekday";
import dayOfYear from "dayjs/plugin/dayOfYear";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);

// Fill in missing months with zeros and add projections
function fillMissingMonths(data: { date: string; count: number }[]): { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] {
    if (data.length === 0) return data;

    const result: { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] = [];
    const monthMap = new Map(data.map(d => [d.date, d.count]));

    // Find the earliest and latest dates
    const dates = data.map(d => d.date).sort();
    const startDate = dayjs(dates[0]);
    const currentMonth = dayjs().format('YYYY-MM');
    const endDate = dayjs(currentMonth);

    // Calculate historical average from completed months (last 6 months, excluding current)
    const completedMonths = data.filter(d => d.date !== currentMonth && d.count > 0);
    const recentMonths = completedMonths.slice(-6); // Last 6 completed months
    const historicalAverage = recentMonths.length > 0
        ? Math.round(recentMonths.reduce((sum, d) => sum + d.count, 0) / recentMonths.length)
        : 0;

    // Calculate variance to determine if data is "flat"
    const variance = recentMonths.length > 1
        ? recentMonths.reduce((sum, d) => sum + Math.pow(d.count - historicalAverage, 2), 0) / recentMonths.length
        : 0;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = historicalAverage > 0 ? stdDev / historicalAverage : 0;
    const isFlat = coefficientOfVariation < 0.25; // Less than 25% variation = relatively flat

    // Generate all months from start to current
    let current = startDate;
    while (current.isBefore(endDate) || current.format('YYYY-MM') === currentMonth) {
        const monthKey = current.format('YYYY-MM');
        const count = monthMap.get(monthKey) || 0;

        if (monthKey === currentMonth && count > 0) {
            const dayOfMonth = dayjs().date();
            const daysInMonth = dayjs().daysInMonth();

            // Calculate linear projection
            const linearProjection = Math.round((count / dayOfMonth) * daysInMonth);

            // For flat data, use historical average as the expected value
            // For growing data, use linear projection
            let projectedCount: number;
            if (isFlat && historicalAverage > 0) {
                // Use historical average, but ensure it's at least the current count
                projectedCount = Math.max(historicalAverage, count);
            } else {
                projectedCount = linearProjection;
            }

            // Hide current month from graph if less than 7 days into the month
            if (dayOfMonth < 7) {
                result.push({
                    date: monthKey,
                    count: projectedCount > count ? projectedCount : count,
                    actualCount: count,
                    isProjected: projectedCount > count,
                    hideFromGraph: true
                });
            } else {
                if (projectedCount > count) {
                    result.push({
                        date: monthKey,
                        count: projectedCount,
                        actualCount: count,
                        isProjected: true
                    });
                } else {
                    result.push({ date: monthKey, count });
                }
            }
        } else {
            result.push({ date: monthKey, count });
        }

        current = current.add(1, 'month');
    }

    return result;
}

export function AccountsGraph() {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [data, setData] = useState<{ date: string, count: number }[]>([]);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedGrowthRate, setSelectedGrowthRate] = useState<number | null>(null);
    const [showCurrentMonth, setShowCurrentMonth] = useState(false);

    const loadActiveAccountsData = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            // Fetch active accounts data from the new endpoint
            const response = await axios.get('/api/v1/app/authenticated/management/accounts-graph');
            const activeAccounts = response.data.data.active_accounts;

            console.log('Fetched active accounts:', activeAccounts);
            setData(activeAccounts);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An unknown error has occurred. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadActiveAccountsData();
    }, [loadActiveAccountsData]);

    // Grouped data calculation must happen before any conditional returns
    const groupedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return fillMissingMonths(data);
    }, [data]);

    // Calculate trend line data
    const { trendLineData, trendInfo } = useMemo(() => {
        const trend = calculateTrendLine(groupedData as any);
        if (trend) {
            console.log('AccountsGraph trend calculated:', {
                type: trend.type,
                dataPoints: groupedData.length,
                firstValue: groupedData[0]?.count,
                lastValue: groupedData[groupedData.length - 1]?.count,
                exponentialR2: trend.exponentialR2,
                linearR2: trend.linearR2,
                cmgr: trend.cmgr
            });

            // Only generate trend line data if the fit is good enough
            const hasMeaningfulTrend = (trend.exponentialR2 && trend.exponentialR2 > 0.7) ||
                                       (trend.linearR2 && trend.linearR2 > 0.5);

            return {
                trendLineData: hasMeaningfulTrend ? generateTrendLineData(groupedData, trend) : null,
                trendInfo: {
                    type: trend.type,
                    exponentialR2: trend.exponentialR2,
                    linearR2: trend.linearR2,
                    cmgr: trend.cmgr
                }
            };
        } else {
            console.log('AccountsGraph: No trend detected for data', {
                dataPoints: groupedData.length,
                firstValue: groupedData[0]?.count,
                lastValue: groupedData[groupedData.length - 1]?.count
            });
            return { trendLineData: null, trendInfo: null };
        }
    }, [groupedData]);

    // Get current period data for display
    const currentPeriodData = groupedData[groupedData.length - 1];
    const hasProjection = currentPeriodData?.isProjected;

    // Calculate total active accounts (sum of all months - note: this is unique accounts per month, not total unique)
    const totalActiveAccountMonths = useMemo(() => {
        return groupedData.reduce((sum, d) => sum + (d.actualCount || d.count), 0);
    }, [groupedData]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadActiveAccountsData();
                    }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Export data as CSV
    const exportData = () => {
        if (!groupedData || groupedData.length === 0) return;

        const csvContent = [
            'Month,Active Accounts',
            ...groupedData.map(item => {
                const count = item.actualCount || item.count;
                return `${item.date},${count}`;
            })
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const timestamp = dayjs().format('YYYY-MM-DD');
        const filename = `active-accounts-monthly-${timestamp}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Get current period label and count for subtitle
    const getCurrentPeriodLabel = () => {
        const current = currentPeriodData;
        if (!current) return null;

        let value = current.isProjected ? current.actualCount : current.count;
        let projected = current.isProjected ? current.count : null;

        if (projected) {
            return `${value} active this month • Projected: ${projected}`;
        }
        return `${value} active this month`;
    };

    return (
        <div className="grid gap-3">
            <div className="card">
                <div className="card-header">
                    <div>
                        <h3 className="card-title">Active Accounts Over Time</h3>
                        {currentPeriodData && (
                            <p className="text-sm text-gray-500 mt-1">{getCurrentPeriodLabel()}</p>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {showAdvanced && (
                            <button
                                onClick={exportData}
                                className="btn btn-sm btn-light"
                                title="Export data as CSV"
                            >
                                <Download className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className={`btn btn-sm ${showAdvanced ? "btn-warning" : "btn-light"}`}
                            title="Toggle advanced options"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </button>
                    </div>
                </div>
                {showAdvanced && (
                    <div className="px-6 py-3 bg-gray-50 border-t border-b border-gray-200 space-y-2">
                        {groupedData.some(d => d.hideFromGraph) && (
                            <div>
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showCurrentMonth}
                                        onChange={(e) => setShowCurrentMonth(e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>Show current month on graph (actual + projection)</span>
                                </label>
                            </div>
                        )}
                        {trendInfo && (
                            <div className="text-sm text-gray-600">
                                <span className="font-semibold">Trend Analysis:</span>
                                <span className="ml-3">
                                    Model: {trendInfo.type === 'exponential' ? 'Exponential' : 'Linear'}
                                </span>
                                <span className="ml-4">
                                    Linear R²: {(trendInfo.linearR2 || 0).toFixed(3)}
                                </span>
                                <span className="ml-4">
                                    Exponential R²: {(trendInfo.exponentialR2 || 0).toFixed(3)}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                <div className="card-body">
                    <div style={{ height: 'max(400px, 50vh)' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={(trendLineData || groupedData).filter(d => showCurrentMonth ? true : !d.hideFromGraph)}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    interval={0}
                                    angle={groupedData.length > 12 ? -45 : 0}
                                    textAnchor={groupedData.length > 12 ? "end" : "middle"}
                                    height={groupedData.length > 12 ? 60 : undefined}
                                    tickFormatter={(value) => dayjs(value).format("MMM 'YY")}
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length > 0) {
                                            const data = payload[0].payload;
                                            const trendValue = payload.find((p: any) => p.dataKey === 'trend');
                                            return (
                                                <div className="bg-white p-2 border border-gray-300 rounded shadow">
                                                    <p className="font-semibold">{dayjs(data.date).format("MMMM YYYY")}</p>
                                                    {data.isProjected ? (
                                                        <>
                                                            <p className="text-amber-600">Current: {data.actualCount} active accounts</p>
                                                            <p className="text-gray-500">Projected: {data.count} active accounts</p>
                                                        </>
                                                    ) : (
                                                        <p>Active accounts: {data.count}</p>
                                                    )}
                                                    {trendValue && trendValue.value !== undefined && (
                                                        <p className="text-gray-400">Trend: {Math.round(Number(trendValue.value))}</p>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                {/* Trend line - render first so it appears behind the main line */}
                                {trendLineData && (
                                    <Line
                                        type="natural"
                                        dataKey="trend"
                                        stroke="#9ca3af"
                                        strokeWidth={1.5}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={false}
                                        isAnimationActive={false}
                                    />
                                )}
                                <Line
                                    type="monotone"
                                    dataKey={(d: any) => (d.hideFromGraph && showCurrentMonth) ? null : d.count}
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    dot={(props: any) => {
                                        const { cx, cy, payload, index } = props;
                                        if (payload.isProjected && !payload.hideFromGraph) {
                                            return <circle key={`dot-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                                        }
                                        if (!payload.hideFromGraph && !payload.isProjected) {
                                            return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill="#10b981" />;
                                        }
                                        return null;
                                    }}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                                {/* Show current month dots when enabled */}
                                {showCurrentMonth && (
                                    <>
                                        {/* Current month projected dot */}
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            stroke="transparent"
                                            strokeWidth={0}
                                            dot={(props: any) => {
                                                const { cx, cy, payload, index } = props;
                                                if (payload.hideFromGraph && payload.isProjected) {
                                                    return <circle key={`dot-current-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                                                }
                                                return null;
                                            }}
                                            connectNulls={false}
                                            isAnimationActive={false}
                                        />
                                        {/* Current month actual dot */}
                                        <Line
                                            type="monotone"
                                            dataKey="actualCount"
                                            stroke="transparent"
                                            strokeWidth={0}
                                            dot={(props: any) => {
                                                const { cx, cy, payload, index } = props;
                                                if (payload.hideFromGraph && payload.actualCount) {
                                                    return <circle key={`dot-current-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                                                }
                                                return null;
                                            }}
                                            connectNulls={false}
                                            isAnimationActive={false}
                                        />
                                    </>
                                )}
                                {/* Add actual value dots for projected months */}
                                <Line
                                    type="monotone"
                                    dataKey="actualCount"
                                    stroke="transparent"
                                    strokeWidth={0}
                                    dot={(props: any) => {
                                        const { cx, cy, payload, index } = props;
                                        if (payload.actualCount && !payload.hideFromGraph) {
                                            return <circle key={`dot-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                                        }
                                        return null;
                                    }}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    {(groupedData.some(d => d.hideFromGraph) || (trendInfo && trendInfo.cmgr !== undefined)) && (
                        <div className="mt-3">
                            <div className="text-sm">
                                {groupedData.some(d => d.hideFromGraph) && !showCurrentMonth && (
                                    <span className="text-gray-500 italic">
                                        Current month will appear on graph after {7 - dayjs().date()} more day{7 - dayjs().date() !== 1 ? 's' : ''}
                                    </span>
                                )}
                                {trendInfo && trendInfo.cmgr !== undefined && (
                                    <>
                                        {groupedData.some(d => d.hideFromGraph) && !showCurrentMonth && <span className="text-gray-400 mx-2">•</span>}
                                        <span className="font-semibold text-green-700">
                                            CMGR: {(trendInfo.cmgr * 100).toFixed(1)}%
                                        </span>
                                    </>
                                )}
                            </div>
                            {trendInfo && trendInfo.cmgr !== undefined && (() => {
                                const currentValue = groupedData.filter(d => !d.hideFromGraph && !d.isProjected)[groupedData.filter(d => !d.hideFromGraph && !d.isProjected).length - 1]?.count || 0;
                                const cmgrPercent = trendInfo.cmgr * 100;
                                const projectedIn12Months = Math.round(currentValue * Math.pow(1 + trendInfo.cmgr, 12));

                                const bands = [
                                    { min: -Infinity, max: 0, midpoint: -5, label: "Declining", color: "text-red-700 bg-red-50" },
                                    { min: 0, max: 5, midpoint: 2.5, label: "Steady (0-5%)", color: "text-yellow-700 bg-yellow-50" },
                                    { min: 5, max: 15, midpoint: 10, label: "Strong (5-15%)", color: "text-green-600 bg-green-50" },
                                    { min: 15, max: 30, midpoint: 22.5, label: "Exceptional (15-30%)", color: "text-green-700 bg-green-100" },
                                    { min: 30, max: Infinity, midpoint: 40, label: "Outstanding (30%+)", color: "text-green-800 bg-green-200" }
                                ];

                                const currentBand = bands.find(b => cmgrPercent >= b.min && cmgrPercent < b.max);
                                const effectiveGrowthRate = selectedGrowthRate !== null ? selectedGrowthRate : cmgrPercent;

                                return (
                                    <div className="mt-2 p-3 bg-emerald-50 rounded-lg text-sm">
                                        <div className="font-semibold text-gray-700 mb-2">Growth Performance</div>
                                        <div className="text-gray-600 space-y-2">
                                            <div className="flex flex-wrap gap-2">
                                                {bands.map((band, idx) => {
                                                    const isActive = band === currentBand;
                                                    const isSelected = selectedGrowthRate !== null &&
                                                                     selectedGrowthRate >= band.min &&
                                                                     selectedGrowthRate < (band.max === Infinity ? Infinity : band.max);
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => {
                                                                if (isActive || isSelected) {
                                                                    setSelectedGrowthRate(null);
                                                                } else {
                                                                    setSelectedGrowthRate(band.midpoint);
                                                                }
                                                            }}
                                                            className={`px-2 py-1 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity ${
                                                                isSelected
                                                                    ? `${band.color} font-bold ring-2 ring-emerald-500`
                                                                    : isActive
                                                                        ? `${band.color} font-semibold ring-2 ring-offset-1 ${band.color.includes('red') ? 'ring-red-400' : band.color.includes('yellow') ? 'ring-yellow-400' : 'ring-green-400'}`
                                                                        : 'bg-gray-100 text-gray-500'
                                                            }`}
                                                        >
                                                            {band.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="pt-2 border-t border-emerald-200">
                                                <span className="font-medium">12-Month Projection:</span> {selectedGrowthRate !== null && Math.abs(selectedGrowthRate - cmgrPercent) > 0.01 ? (
                                                    <>
                                                        At <span className="font-semibold text-emerald-700">{selectedGrowthRate.toFixed(1)}%</span> monthly growth rate, we'd have approximately <span className="font-semibold text-green-700">{Math.round(currentValue * Math.pow(1 + selectedGrowthRate / 100, 12)).toLocaleString()}</span> active accounts per month in one year (up from {currentValue.toLocaleString()} today). <button onClick={() => setSelectedGrowthRate(null)} className="text-xs text-emerald-600 underline hover:text-emerald-800 ml-1">Reset to actual</button>
                                                    </>
                                                ) : (
                                                    <>
                                                        If we maintain this {cmgrPercent.toFixed(1)}% monthly growth rate, we'll have approximately <span className="font-semibold text-green-700">{projectedIn12Months.toLocaleString()}</span> active accounts per month in one year (up from {currentValue.toLocaleString()} today).
                                                    </>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-2">
                                                <a
                                                    href="https://www.wallstreetprep.com/knowledge/compound-monthly-growth-rate-cmgr/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-emerald-600 underline hover:text-emerald-800"
                                                >
                                                    Learn more about CMGR
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
